import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2 } from 'lucide-react';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/common/components/ui/dialog';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import { organizationQueryKeys } from '@/features/organizations/queries';
import { OrganizationService } from '@/features/organizations/services/OrganizationService';
import { useCountries } from '@/features/auth/hooks/useCountries';
import type { ModalRenderProps } from '@/common/components/modal-provider/types';

type Props = Pick<ModalRenderProps<void>, 'resolve'>;

const createOrgSchema = z.object({
  name: requiredInput(z.string().max(100)),
  country: requiredInput(z.string()),
  street: requiredInput(z.string().max(255)),
  city: requiredInput(z.string().max(100)),
  zipCode: requiredInput(z.string().max(20)),
});

export function CreateOrgDialog({ resolve }: Props) {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const countries = useCountries();

  const form = useAppForm(
    appFormOptions({
      schema: createOrgSchema,
      defaultValues: {
        name: null,
        country: 'CZ',
        street: null,
        city: null,
        zipCode: null,
      },
      onSubmit: async ({ value }) => {
        await OrganizationService.create(
          {
            name: value.name,
            country: value.country,
            street: value.street,
            city: value.city,
            zipCode: value.zipCode,
          },
          currentUser.email
        );
        await queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
        resolve();
      },
    })
  );

  return (
    <form.AppForm>
      <form.FormElement>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
              <Building2 size={20} className="text-primary" />
            </div>
            <div>
              <DialogTitle>
                <Trans>Create organization</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>You will automatically become the administrator.</Trans>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <FormErrors />

        <div className="space-y-4 py-8">
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                label={<Trans>Company name</Trans>}
                placeholder={t`e.g. My Company Ltd.`}
                required
              />
            )}
          </form.AppField>

          <form.AppField name="country">
            {(field) => (
              <field.SelectField label={<Trans>Country</Trans>} required>
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="street">
            {(field) => (
              <field.TextField
                required
                label={<Trans>Street and number</Trans>}
                placeholder={t`e.g. Main Street 1`}
              />
            )}
          </form.AppField>

          <div className="grid grid-cols-2 gap-3">
            <form.AppField name="city">
              {(field) => (
                <field.TextField
                  required
                  label={<Trans>City</Trans>}
                  placeholder={t`e.g. Prague`}
                />
              )}
            </form.AppField>

            <form.AppField name="zipCode">
              {(field) => (
                <field.TextField required label={<Trans>Postal code</Trans>} placeholder="110 00" />
              )}
            </form.AppField>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => resolve()}>
            <Trans>Cancel</Trans>
          </Button>
          <FormSubmit>
            <Trans>Create organization</Trans>
          </FormSubmit>
        </DialogFooter>
      </form.FormElement>
    </form.AppForm>
  );
}
